import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Copy, ExternalLink, Check, ArrowLeft, Smartphone, Car, Users, Share2, QrCode } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const LinksPage = () => {
  const navigate = useNavigate();
  const logoUrl = 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg';
  const baseUrl = window.location.origin;
  const [copiedLink, setCopiedLink] = useState('');

  const links = {
    main: [
      { name: 'Landing Page', path: '/', description: 'Main homepage with all services' },
      { name: 'Drivers Recruitment', path: '/drivers', description: 'Drive & Earn with J-Style Services' }
    ],
    user: [
      { name: 'User Portal', path: '/user', description: '📱 Add to Home Screen - Login, signup & book rides or deliveries', highlight: true }
    ],
    driver: [
      { name: 'Driver Portal', path: '/driver', description: '📱 Add to Home Screen - Login, signup & start earning', highlight: true }
    ]
  };

  const copyToClipboard = (path) => {
    const fullUrl = `${baseUrl}${path}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedLink(path);
    toast({
      title: "Link Copied!",
      description: "URL has been copied to clipboard",
    });
    setTimeout(() => setCopiedLink(''), 2000);
  };

  const openInNewTab = (path) => {
    window.open(`${baseUrl}${path}`, '_blank');
  };

  const shareLink = async (name, path) => {
    const fullUrl = `${baseUrl}${path}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `J-Style Services - ${name}`,
          text: `Check out ${name} on J-Style Services!`,
          url: fullUrl
        });
      } catch (err) {
        copyToClipboard(path);
      }
    } else {
      copyToClipboard(path);
    }
  };

  const LinkCard = ({ name, path, description, highlight }) => {
    const fullUrl = `${baseUrl}${path}`;
    const isCopied = copiedLink === path;

    return (
      <Card className={`p-5 hover:shadow-md transition-all border-2 ${highlight ? 'border-amber-400 bg-amber-50/50' : ''}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-bold text-lg">{name}</h3>
              {highlight && <Smartphone size={16} className="text-amber-600" />}
            </div>
            <p className="text-sm text-gray-600 mb-3">{description}</p>
            <div className="bg-slate-50 rounded-lg p-3 border">
              <code className="text-sm text-slate-700 break-all">{fullUrl}</code>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={() => shareLink(name, path)}
              className="border-2 border-blue-500 hover:bg-blue-50"
              title="Share"
            >
              <Share2 size={16} className="text-blue-600" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => copyToClipboard(path)}
              className={`border-2 ${isCopied ? 'border-green-500 bg-green-50' : 'border-amber-600 hover:bg-amber-50'}`}
              title="Copy"
            >
              {isCopied ? <Check size={16} className="text-green-600" /> : <Copy size={16} className="text-amber-600" />}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => openInNewTab(path)}
              className="border-2 border-slate-600 hover:bg-slate-50"
              title="Open"
            >
              <ExternalLink size={16} className="text-slate-600" />
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="flex-shrink-0">
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-3">
              <img src={logoUrl} alt="Logo" className="h-10 w-10 object-contain flex-shrink-0" />
              <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent whitespace-nowrap">J-Style Services</span>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-slate-800 via-amber-600 to-yellow-500 bg-clip-text text-transparent">
              Shareable Links
            </h1>
            <p className="text-gray-600 text-lg mb-4">
              Copy and share links to specific pages of J-Style Services
            </p>
            {/* QR Codes Button */}
            <Button
              onClick={() => navigate('/qr-codes')}
              className="bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-700 hover:to-slate-600 text-white"
            >
              <QrCode size={18} className="mr-2" />
              Download QR Codes for Flyers
            </Button>
          </div>

          {/* Add to Home Screen Instructions */}
          <Card className="p-6 mb-10 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Smartphone className="text-amber-600" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">📱 Add to Home Screen</h3>
                <p className="text-gray-700 mb-3">Share the highlighted links below with drivers and users. They can add the app to their home screen:</p>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-white p-3 rounded-lg">
                    <p className="font-semibold mb-1">iPhone/iPad (Safari):</p>
                    <ol className="text-gray-600 space-y-1">
                      <li>1. Open link in Safari</li>
                      <li>2. Tap Share button (square with arrow)</li>
                      <li>3. Scroll down, tap "Add to Home Screen"</li>
                      <li>4. Tap "Add"</li>
                    </ol>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="font-semibold mb-1">Android (Chrome):</p>
                    <ol className="text-gray-600 space-y-1">
                      <li>1. Open link in Chrome</li>
                      <li>2. Tap ⋮ menu (3 dots)</li>
                      <li>3. Tap "Add to Home screen"</li>
                      <li>4. Tap "Add"</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Social Media Sharing Cards */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold mb-2 text-center">🔗 Share on Social Media</h2>
            <p className="text-gray-600 text-center mb-6">Copy these direct links to promote on Instagram, Facebook, TikTok, etc.</p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Rider Portal Link */}
              <Card className="p-6 border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-yellow-50 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl"></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <Users className="text-white" size={28} />
                    </div>
                    <div>
                      <h3 className="font-bold text-xl text-gray-900">Rider Portal</h3>
                      <p className="text-sm text-amber-700">For customers - book rides & deliveries</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border-2 border-amber-200 mb-4 shadow-sm">
                    <code className="text-sm text-slate-800 font-medium break-all">{baseUrl}/user</code>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 shadow-md" 
                      onClick={() => shareLink('Rider Portal', '/user')}
                    >
                      <Share2 size={18} className="mr-2" />Share Link
                    </Button>
                    <Button 
                      variant="outline" 
                      className="border-2 border-amber-500 hover:bg-amber-50"
                      onClick={() => copyToClipboard('/user')}
                    >
                      {copiedLink === '/user' ? <Check size={18} className="text-green-600" /> : <Copy size={18} className="text-amber-600" />}
                    </Button>
                    <Button 
                      variant="outline"
                      className="border-2"
                      onClick={() => openInNewTab('/user')}
                    >
                      <ExternalLink size={18} />
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Driver Portal Link */}
              <Card className="p-6 border-2 border-blue-400 bg-gradient-to-br from-blue-50 to-indigo-50 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl"></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center shadow-lg">
                      <Car className="text-white" size={28} />
                    </div>
                    <div>
                      <h3 className="font-bold text-xl text-gray-900">Driver Portal</h3>
                      <p className="text-sm text-blue-700">For drivers - login & start earning</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border-2 border-blue-200 mb-4 shadow-sm">
                    <code className="text-sm text-slate-800 font-medium break-all">{baseUrl}/driver</code>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-md" 
                      onClick={() => shareLink('Driver Portal', '/driver')}
                    >
                      <Share2 size={18} className="mr-2" />Share Link
                    </Button>
                    <Button 
                      variant="outline" 
                      className="border-2 border-blue-500 hover:bg-blue-50"
                      onClick={() => copyToClipboard('/driver')}
                    >
                      {copiedLink === '/driver' ? <Check size={18} className="text-green-600" /> : <Copy size={18} className="text-blue-600" />}
                    </Button>
                    <Button 
                      variant="outline"
                      className="border-2"
                      onClick={() => openInNewTab('/driver')}
                    >
                      <ExternalLink size={18} />
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Quick Copy Cards for Portals */}
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <Card className="p-6 border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                  <Car className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-lg">For Drivers</h3>
                  <p className="text-sm text-gray-600">Share with your drivers</p>
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 border mb-4">
                <code className="text-sm text-slate-700 break-all">{baseUrl}/driver</code>
              </div>
              <div className="flex gap-2">
                <Button className="flex-1 bg-amber-500 hover:bg-amber-600" onClick={() => shareLink('Driver Portal', '/driver')}>
                  <Share2 size={16} className="mr-2" />Share
                </Button>
                <Button variant="outline" onClick={() => copyToClipboard('/driver')}>
                  <Copy size={16} />
                </Button>
              </div>
            </Card>

            <Card className="p-6 border-2 border-blue-400 bg-gradient-to-br from-blue-50 to-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                  <Users className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-lg">For Users/Customers</h3>
                  <p className="text-sm text-gray-600">Share with your customers</p>
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 border mb-4">
                <code className="text-sm text-slate-700 break-all">{baseUrl}/user</code>
              </div>
              <div className="flex gap-2">
                <Button className="flex-1 bg-blue-500 hover:bg-blue-600" onClick={() => shareLink('User Portal', '/user')}>
                  <Share2 size={16} className="mr-2" />Share
                </Button>
                <Button variant="outline" onClick={() => copyToClipboard('/user')}>
                  <Copy size={16} />
                </Button>
              </div>
            </Card>
          </div>

          {/* Main Links */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <div className="w-2 h-8 bg-gradient-to-b from-amber-500 to-yellow-600 rounded-full" />
              Main Pages
            </h2>
            <div className="space-y-4">
              {links.main.map((link) => (
                <LinkCard key={link.path} {...link} />
              ))}
            </div>
          </div>

          {/* User Links */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full" />
              <Users size={24} className="text-blue-600" />
              User Pages
            </h2>
            <div className="space-y-4">
              {links.user.map((link) => (
                <LinkCard key={link.path} {...link} />
              ))}
            </div>
          </div>

          {/* Driver Links */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <div className="w-2 h-8 bg-gradient-to-b from-amber-500 to-yellow-600 rounded-full" />
              <Car size={24} className="text-amber-600" />
              Driver Pages
            </h2>
            <div className="space-y-4">
              {links.driver.map((link) => (
                <LinkCard key={link.path} {...link} />
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <Card className="p-8 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 text-white border-2 border-slate-700 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 rounded-full blur-3xl" />
            <div className="relative z-10 text-center">
              <h3 className="text-2xl font-bold mb-3">Ready to Test?</h3>
              <p className="text-slate-300 mb-6">
                Open the app on your phone, login, and add to home screen for the best experience
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Button 
                  onClick={() => navigate('/')}
                  className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white"
                >
                  Go to Homepage
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/user')}
                  className="bg-white text-slate-900 hover:bg-slate-100 border-2"
                >
                  Book a Ride
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LinksPage;
